import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FaHeart, FaComment, FaEye, FaShare, FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp, FaCopy, FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import Slider from "react-slick";
// Components
import TopNavbar from "../components/Nav/TopNavbar";
import FullButton from "../components/Buttons/FullButton";
// Services
import { getBlogById, trackBlogView, getBlogComments, submitComment } from "../services/blogApi";

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

// Calculate read time from content
const calculateReadTime = (content) => {
  if (!content) return '1 min read';
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200); // Average reading speed: 200 words per minute
  return `${minutes} min read`;
};

// Format numbers to K, M, B format
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  const number = typeof num === 'string' ? parseInt(num, 10) : num;
  
  if (number >= 1000000000) {
    return (number / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (number >= 1000000) {
    return (number / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (number >= 1000) {
    return (number / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return number.toString();
};

export default function BlogDetail({ match }) {
  // Extract and clean blogId from route params
  const rawBlogId = match?.params?.id;
  const blogId = rawBlogId ? rawBlogId.trim() : null;
  
  // State for blog data
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for interactive features
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [views, setViews] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [comments, setComments] = useState(0);
  const [commentsList, setCommentsList] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentName, setCommentName] = useState("");
  const [commentEmail, setCommentEmail] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  
  // Memoize carousel settings to prevent re-initialization
  const carouselSettings = useMemo(() => ({
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    fade: false,
    cssEase: 'linear',
    adaptiveHeight: false,
  }), []);
  
  // Fetch blog data
  useEffect(() => {
    const fetchBlog = async () => {
      if (!blogId) {
        console.error('BlogDetail: No blog ID provided');
        setError('Blog ID is required');
        setLoading(false);
        return;
      }
      
      console.log('BlogDetail: Fetching blog with ID:', blogId);
      
      try {
        setLoading(true);
        setError(null);
        const response = await getBlogById(blogId);
        
        console.log('BlogDetail: API response:', response);
        
        if (response && response.data) {
          const blogData = response.data;
          console.log('BlogDetail: Setting blog data:', blogData);
          setBlog(blogData);
          setLikes(blogData.likes || 0);
          setViews(blogData.views || 0);
          setComments(blogData.comments || 0);
        } else {
          console.error('BlogDetail: Invalid response format:', response);
          setError('Blog not found');
        }
      } catch (err) {
        console.error('BlogDetail: Error fetching blog:', err);
        setError(err.message || 'Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBlog();
  }, [blogId]);
  
  // Track blog view when blog is loaded
  useEffect(() => {
    if (blog && blog.id) {
      trackBlogView(blog.id);
    }
  }, [blog]);
  
  // Fetch comments when blog is loaded
  useEffect(() => {
    const fetchComments = async () => {
      if (!blog || !blog.id) return;
      
      try {
        setCommentsLoading(true);
        const response = await getBlogComments(blog.id, { skip: 0, limit: 20 });
        
        if (response && response.data) {
          setCommentsList(response.data);
          // Use pagination total if available, otherwise use data length
          const totalComments = response.pagination?.total || response.data.length;
          setComments(totalComments);
        }
      } catch (err) {
        console.error('Error fetching comments:', err);
        // Don't show error to user, just log it
      } finally {
        setCommentsLoading(false);
      }
    };
    
    fetchComments();
  }, [blog]);
  
  // Memoize computed values to prevent unnecessary re-renders (must be before any returns)
  const readTime = useMemo(() => {
    return blog ? calculateReadTime(blog.content) : '1 min read';
  }, [blog]);
  
  const formattedDate = useMemo(() => {
    return blog ? formatDate(blog.created_at) : '';
  }, [blog]);
  
  // Memoize image URLs to prevent carousel re-initialization
  const imageUrls = useMemo(() => {
    return blog?.images && blog.images.length > 0 ? blog.images : [];
  }, [blog?.images]);
  
  const hasMultipleImages = useMemo(() => {
    return imageUrls.length > 1;
  }, [imageUrls.length]);
  
  // Debug: Log image URLs (only once when they change)
  useEffect(() => {
    if (imageUrls.length > 0) {
      console.log('BlogDetail: Image URLs from API:', imageUrls);
      console.log('BlogDetail: Has multiple images:', hasMultipleImages);
    }
  }, [imageUrls, hasMultipleImages]);
  
  // Handle like functionality
  const handleLike = () => {
    if (isLiked) {
      setLikes(prev => prev - 1);
      setIsLiked(false);
    } else {
      setLikes(prev => prev + 1);
      setIsLiked(true);
    }
  };
  
  // Handle share functionality
  const handleShare = (platform) => {
    if (!blog) return;
    const url = window.location.href;
    const title = blog.title;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        setNotification({ show: true, message: 'Link copied to clipboard!', type: 'success' });
        setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
        break;
      default:
        break;
    }
    setShowShareMenu(false);
  };
  
  // Handle comment button click - scroll to comments
  const handleCommentClick = () => {
    const commentsSection = document.getElementById('comments-section');
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Handle comment form submission - show modal first
  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      setShowCommentModal(true);
    }
  };
  
  // Handle actual comment submission after modal is filled
  const handleSubmitComment = async () => {
    if (!commentName.trim() || !commentEmail.trim()) {
      setNotification({ show: true, message: 'Please fill in both name and email', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
      return;
    }
    
    if (!blog || !blog.id) {
      setNotification({ show: true, message: 'Blog ID is missing', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(commentEmail)) {
      setNotification({ show: true, message: 'Please enter a valid email address', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
      return;
    }
    
    try {
      setSubmittingComment(true);
      const commentData = {
        comment_text: newComment,
        name: commentName.trim(),
        email: commentEmail.trim()
      };
      
      const response = await submitComment(blog.id, commentData);
      
      if (response && response.status === 'success') {
        // Clear form
        setNewComment("");
        setCommentName("");
        setCommentEmail("");
        setShowCommentModal(false);
        
        // Refresh comments list
        const commentsResponse = await getBlogComments(blog.id, { skip: 0, limit: 20 });
        if (commentsResponse && commentsResponse.data) {
          setCommentsList(commentsResponse.data);
          const totalComments = commentsResponse.pagination?.total || commentsResponse.data.length;
          setComments(totalComments);
        }
        
        setNotification({ show: true, message: 'Comment submitted successfully!', type: 'success' });
        setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
      }
    } catch (err) {
      console.error('Error submitting comment:', err);
      setNotification({ show: true, message: 'Failed to submit comment. Please try again.', type: 'error' });
      setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
    } finally {
      setSubmittingComment(false);
    }
  };
  
  // Close modal
  const handleCloseModal = () => {
    setShowCommentModal(false);
    setCommentName("");
    setCommentEmail("");
  };
  
  if (loading) {
    return (
      <>
        <TopNavbar />
        <Wrapper>
          <div className="container">
            <LoadingContainer>
              <LoadingSpinner />
              <p>Loading blog post...</p>
            </LoadingContainer>
          </div>
        </Wrapper>
      </>
    );
  }
  
  if (error || !blog) {
    return (
      <>
        <TopNavbar />
        <Wrapper>
          <div className="container">
            <ErrorContainer>
              <h2>Error</h2>
              <p>{error || 'Blog post not found'}</p>
              <p style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.8 }}>
                Blog ID: {blogId || 'Not provided'}
              </p>
              <Link to="/" style={{ marginTop: '20px', display: 'inline-block' }}>
                <FullButton title="Back to Home" />
              </Link>
            </ErrorContainer>
          </div>
        </Wrapper>
      </>
    );
  }
  
  // Safety check - should never reach here if blog is null, but just in case
  if (!blog) {
    return null;
  }
  
  // Helper function to get full image URL
  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    
    // If image URL is already absolute, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If image URL starts with /storage, it's from the API
    if (imageUrl.startsWith('/storage/')) {
      const apiBaseUrl = process.env.NODE_ENV === 'production' 
        ? (process.env.REACT_APP_API_BASE_URL || 'https://api.afriqcm.com')
        : (typeof window !== 'undefined' ? window.location.origin : 'https://api.afriqcm.com');
      return `${apiBaseUrl}${imageUrl}`;
    }
    
    // If it's a relative path starting with /, make it absolute
    if (imageUrl.startsWith('/')) {
      const origin = typeof window !== 'undefined' ? window.location.origin : 'https://afriqcm.com';
      return `${origin}${imageUrl}`;
    }
    
    // Otherwise, assume it's relative to the site root
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://afriqcm.com';
    return `${origin}/${imageUrl}`;
  };

  // Helper function to strip HTML and get plain text
  const getPlainText = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  // Get blog meta data
  const blogTitle = blog?.title || 'AFRIQ Blog';
  const blogDescription = blog?.meta_description || (blog?.content ? getPlainText(blog.content).substring(0, 160) : 'Read this article on AFRIQ Bamenda');
  const blogImage = blog?.images && blog.images.length > 0 ? getFullImageUrl(blog.images[0]) : '';
  const blogUrl = typeof window !== 'undefined' ? window.location.href : '';
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://afriqcm.com';

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{blogTitle} | AFRIQ Bamenda</title>
        <meta name="description" content={blogDescription} />
        {blog?.meta_keywords && <meta name="keywords" content={blog.meta_keywords} />}
        <link rel="canonical" href={blogUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={blogUrl} />
        <meta property="og:title" content={blogTitle} />
        <meta property="og:description" content={blogDescription} />
        {blogImage && <meta property="og:image" content={blogImage} />}
        <meta property="og:site_name" content="AFRIQ Bamenda" />
        <meta property="og:locale" content="en_US" />
        {blog?.created_at && <meta property="article:published_time" content={new Date(blog.created_at).toISOString()} />}
        {blog?.updated_at && <meta property="article:modified_time" content={new Date(blog.updated_at).toISOString()} />}
        {blog?.category && <meta property="article:section" content={blog.category} />}
        {blog?.tags && blog.tags.length > 0 && blog.tags.map((tag, idx) => (
          <meta key={idx} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={blogUrl} />
        <meta name="twitter:title" content={blogTitle} />
        <meta name="twitter:description" content={blogDescription} />
        {blogImage && <meta name="twitter:image" content={blogImage} />}
        
        {/* WhatsApp and other platforms */}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        {blogImage && <meta property="og:image:alt" content={blogTitle} />}
        
        {/* Apple Touch Icon */}
        <link rel="apple-touch-icon" href={`${siteUrl}/logo512.png`} />
        <link rel="apple-touch-icon" sizes="180x180" href={`${siteUrl}/logo512.png`} />
        <link rel="apple-touch-icon" sizes="512x512" href={`${siteUrl}/logo512.png`} />
        
        {/* Structured Data - Article Schema */}
        {blog && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BlogPosting",
              "headline": blogTitle,
              "description": blogDescription,
              "image": blogImage ? [blogImage] : [],
              "datePublished": blog.created_at,
              "dateModified": blog.updated_at || blog.created_at,
              "author": {
                "@type": "Organization",
                "name": "AFRIQ Bamenda",
                "url": siteUrl
              },
              "publisher": {
                "@type": "Organization",
                "name": "AFRIQ Bamenda",
                "logo": {
                  "@type": "ImageObject",
                  "url": `${siteUrl}/logo512.png`
                }
              },
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": blogUrl
              },
              "articleSection": blog.category || "Blog",
              "keywords": blog.meta_keywords || blog.tags?.join(", ") || "",
              "wordCount": blog.content ? getPlainText(blog.content).split(/\s+/).length : 0
            })}
          </script>
        )}
        
        {/* Structured Data - Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": siteUrl
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": `${siteUrl}/blogs`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": blogTitle,
                "item": blogUrl
              }
            ]
          })}
        </script>
      </Helmet>
      <TopNavbar />
      <Wrapper>
        <div className="container">
          <Breadcrumb>
            <Link to="/">Home</Link> / <Link to="/#blog">Blog</Link> / <span>{blog.category || 'Blog'}</span>
          </Breadcrumb>
          
          <ArticleHeader>
            <Tag className="afriqGradientBg whiteColor">{blog.category || 'Blog'}</Tag>
            <h1 className="font40 extraBold">{blog.title}</h1>
            <MetaInfo>
              <div className="meta-left">
                <span className="font15 semiBold">By AFRIQ Admin</span>
                <span className="font13 greyColor">{formattedDate}</span>
                <span className="font13 greyColor">•</span>
                <span className="font13 greyColor">{readTime}</span>
              </div>
              <EngagementStats>
                <StatItem onClick={handleLike} className={isLiked ? 'liked' : ''}>
                  <FaHeart />
                  <span>{formatNumber(likes)}</span>
                </StatItem>
                <StatItem onClick={handleCommentClick}>
                  <FaComment />
                  <span>{formatNumber(comments)}</span>
                </StatItem>
                <StatItem>
                  <FaEye />
                  <span>{formatNumber(views)}</span>
                </StatItem>
                <ShareButton onClick={() => setShowShareMenu(!showShareMenu)}>
                  <FaShare />
                </ShareButton>
              </EngagementStats>
            </MetaInfo>
            
            {showShareMenu && (
              <ShareMenu>
                <ShareOption onClick={() => handleShare('facebook')}>
                  <FaFacebook />
                  <span>Facebook</span>
                </ShareOption>
                <ShareOption onClick={() => handleShare('twitter')}>
                  <FaTwitter />
                  <span>Twitter</span>
                </ShareOption>
                <ShareOption onClick={() => handleShare('linkedin')}>
                  <FaLinkedin />
                  <span>LinkedIn</span>
                </ShareOption>
                <ShareOption onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp />
                  <span>WhatsApp</span>
                </ShareOption>
                <ShareOption onClick={() => handleShare('copy')}>
                  <FaCopy />
                  <span>Copy Link</span>
                </ShareOption>
              </ShareMenu>
            )}
          </ArticleHeader>
          
          <MainContent>
            <ArticleWrapper>
              {imageUrls && imageUrls.length > 0 && (
                <FeaturedImage>
                  {hasMultipleImages ? (
                    <>
                      <ImageCarousel>
                        <Slider 
                          key={`carousel-${blog.id}-${imageUrls.length}`}
                          {...carouselSettings}
                        >
                          {imageUrls.map((image, index) => (
                            <div key={`image-${index}-${image}`}>
                              <CarouselImage 
                                src={image} 
                                alt={`${blog.title} - Image ${index + 1}`}
                                onError={(e) => {
                                  console.error('Failed to load image:', image);
                                  e.target.style.background = 'rgba(0, 0, 0, 0.3)';
                                  e.target.style.display = 'flex';
                                  e.target.style.alignItems = 'center';
                                  e.target.style.justifyContent = 'center';
                                  e.target.style.color = 'white';
                                  e.target.alt = 'Image failed to load';
                                }}
                                onLoad={() => {
                                  console.log('Image loaded successfully:', image);
                                }}
                              />
                            </div>
                          ))}
                        </Slider>
                      </ImageCarousel>
                      <ImageOverlay>
                        <ReadTimeBadge>{readTime}</ReadTimeBadge>
                      </ImageOverlay>
                    </>
                  ) : (
                    <>
                      <SingleImage 
                        src={imageUrls[0]} 
                        alt={blog.title}
                        onError={(e) => {
                          console.error('Failed to load image:', imageUrls[0]);
                          e.target.style.background = 'rgba(0, 0, 0, 0.3)';
                          e.target.style.display = 'flex';
                          e.target.style.alignItems = 'center';
                          e.target.style.justifyContent = 'center';
                          e.target.style.color = 'white';
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', imageUrls[0]);
                        }}
                      />
                      <ImageOverlay>
                        <ReadTimeBadge>{readTime}</ReadTimeBadge>
                      </ImageOverlay>
                    </>
                  )}
                </FeaturedImage>
              )}
              
              <ArticleContent>
                <div dangerouslySetInnerHTML={{ __html: blog.content || '' }} />
              </ArticleContent>
              
              {blog.tags && blog.tags.length > 0 && (
                <TagsSection>
                  <TagsLabel>Tags:</TagsLabel>
                  {blog.tags.map((tag, index) => (
                    <TagBadge key={index}>{tag}</TagBadge>
                  ))}
                </TagsSection>
              )}
            </ArticleWrapper>
            
            <Sidebar>
              <SidebarCard>
                <SidebarTitle>Article Info</SidebarTitle>
                <InfoList>
                  <InfoItem>
                    <strong>Category:</strong> {blog.category || 'N/A'}
                  </InfoItem>
                  <InfoItem>
                    <strong>Published:</strong> {formattedDate}
                  </InfoItem>
                  <InfoItem>
                    <strong>Read Time:</strong> {readTime}
                  </InfoItem>
                  {blog.meta_keywords && (
                    <InfoItem>
                      <strong>Keywords:</strong> {blog.meta_keywords}
                    </InfoItem>
                  )}
                </InfoList>
              </SidebarCard>
              
              <SidebarCard>
                <SidebarTitle>Follow AFRIQ</SidebarTitle>
                <SocialLinks>
                  <SocialLink href="#" target="_blank">
                    <FaFacebook />
                    <span>Facebook</span>
                  </SocialLink>
                  <SocialLink href="#" target="_blank">
                    <FaTwitter />
                    <span>Twitter</span>
                  </SocialLink>
                  <SocialLink href="#" target="_blank">
                    <FaLinkedin />
                    <span>LinkedIn</span>
                  </SocialLink>
                  <SocialLink href="#" target="_blank">
                    <FaWhatsapp />
                    <span>WhatsApp</span>
                  </SocialLink>
                </SocialLinks>
              </SidebarCard>
            </Sidebar>
          </MainContent>
          
          {/* Comments Section */}
          <CommentsSection id="comments-section">
              <CommentsHeader>
                <h3 className="font25 extraBold">Comments ({formatNumber(comments)})</h3>
                <p className="font15 greyColor">Share your thoughts and join the discussion</p>
              </CommentsHeader>
              
              <CommentForm onSubmit={handleCommentSubmit}>
                <CommentInputWrapper>
                  <CommentInput
                    placeholder="What are your thoughts on this article?"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <CommentActions>
                    <CommentButton type="submit" disabled={!newComment.trim()}>
                      <span>Post Comment</span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 4L20 12L12 20M20 12H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </CommentButton>
                  </CommentActions>
                </CommentInputWrapper>
              </CommentForm>
              
              {commentsLoading ? (
                <CommentsLoading>
                  <LoadingSpinner />
                  <p>Loading comments...</p>
                </CommentsLoading>
              ) : commentsList.length === 0 ? (
                <EmptyComments>
                  <p>No comments yet. Be the first to comment!</p>
                </EmptyComments>
              ) : (
                <CommentsList>
                  {commentsList.map((comment) => {
                    const initials = comment.name
                      ? comment.name
                          .split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase()
                          .substring(0, 2)
                      : 'U';
                    const commentDate = formatDate(comment.created_at);
                    
                    return (
                      <CommentItem key={comment.id}>
                        <CommentAvatar>
                          <span>{initials}</span>
                        </CommentAvatar>
                        <CommentContent>
                          <CommentHeader>
                            <div className="user-info">
                              <span className="font15 semiBold">{comment.name || 'Anonymous'}</span>
                            </div>
                            <div className="comment-meta">
                              <span className="font12 greyColor">{commentDate}</span>
                            </div>
                          </CommentHeader>
                          <CommentText>
                            {comment.comment_text}
                          </CommentText>
                        </CommentContent>
                      </CommentItem>
                    );
                  })}
                </CommentsList>
              )}
              
              <LoadMoreComments>
                <LoadMoreButton>
                  <span>Load More Comments</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </LoadMoreButton>
              </LoadMoreComments>
            </CommentsSection>
          
          {/* Comment Modal */}
          {showCommentModal && (
            <ModalOverlay onClick={handleCloseModal}>
              <CommentModal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                  <h3 className="font20 extraBold">Add Your Comment</h3>
                  <CloseButton onClick={handleCloseModal}>
                    <FaTimes />
                  </CloseButton>
                </ModalHeader>
                <ModalContent>
                  <ModalInput
                    type="text"
                    placeholder="Your Name *"
                    value={commentName}
                    onChange={(e) => setCommentName(e.target.value)}
                    required
                  />
                  <ModalInput
                    type="email"
                    placeholder="Your Email *"
                    value={commentEmail}
                    onChange={(e) => setCommentEmail(e.target.value)}
                    required
                  />
                  <ModalTextarea
                    placeholder="Your Comment *"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows="4"
                  />
                </ModalContent>
                <ModalActions>
                  <ModalButton onClick={handleCloseModal} type="button" className="cancel">
                    Cancel
                  </ModalButton>
                  <ModalButton 
                    onClick={handleSubmitComment} 
                    type="button" 
                    disabled={submittingComment || !commentName.trim() || !commentEmail.trim() || !newComment.trim()}
                  >
                    {submittingComment ? 'Submitting...' : 'Submit Comment'}
                  </ModalButton>
                </ModalActions>
              </CommentModal>
            </ModalOverlay>
          )}
          
          {/* Notification Toast */}
          {notification.show && (
            <NotificationToast type={notification.type}>
              <NotificationMessage>{notification.message}</NotificationMessage>
              <NotificationClose onClick={() => setNotification({ show: false, message: '', type: 'success' })}>
                <FaTimes />
              </NotificationClose>
            </NotificationToast>
          )}
          
          <AuthorSection>
            <AuthorCard>
              <AuthorAvatar>
                <span className="font25 extraBold purpleColor">AA</span>
              </AuthorAvatar>
              <AuthorInfo>
                <h4 className="font18 extraBold">AFRIQ Admin</h4>
                <p className="font13 greyColor">AFRIQ Administration</p>
                <p className="font13">
                  Part of the AFRIQ Bamenda administration team
                </p>
              </AuthorInfo>
            </AuthorCard>
          </AuthorSection>
          
          <CallToAction>
            <h3 className="font25 extraBold">Ready to Start Your Academic Journey?</h3>
            <p className="font15">Join AFRIQ Bamenda and get the preparation you need to succeed in your chosen field.</p>
            <ButtonGroup>
              <FullButton title="Apply for AFRIQ PREPA" to="/apply" />
              <FullButton title="Join AFRIQ BUSINESS" border />
            </ButtonGroup>
          </CallToAction>
        </div>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  padding-top: 100px;
  min-height: 100vh;
  background: #4b2e83;
`;

const Breadcrumb = styled.div`
  margin-bottom: 30px;
  font-size: 14px;
  
  a {
    color: #7620FF;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ArticleHeader = styled.div`
  margin-bottom: 40px;
  color: white;
  
  h1 {
    color: white;
  }
`;

const Tag = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const MetaInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.9);
  
  .meta-left {
    display: flex;
    gap: 15px;
    align-items: center;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
`;

const EngagementStats = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  background: rgba(75, 46, 131, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(75, 46, 131, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 600;
  
  &:hover {
    background: rgba(75, 46, 131, 0.1);
    transform: translateY(-2px);
  }
  
  &.liked {
    background: rgba(75, 46, 131, 0.1);
    color: #4b2e83;
    
    svg {
      color: #4b2e83;
    }
  }
  
  svg {
    font-size: 16px;
    color: white;
  }
`;

const ShareButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #4b2e83;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
  }
  
  svg {
    font-size: 16px;
  }
`;

const ShareMenu = styled.div`
  position: absolute;
  top: 50px;
  right: 0;
  background: #4b2e83;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(75, 46, 131, 0.1);
  padding: 10px;
  z-index: 100;
  min-width: 180px;
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 20px;
    width: 16px;
    height: 16px;
    background: #4b2e83;
    border: 1px solid rgba(75, 46, 131, 0.1);
    border-bottom: none;
    border-right: none;
    transform: rotate(45deg);
  }
`;

const ShareOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: rgba(75, 46, 131, 0.05);
  }
  
  svg {
    font-size: 18px;
    color: white;
  }
  
  &:nth-child(1) svg { color: #1877f2; }
  &:nth-child(2) svg { color: #1da1f2; }
  &:nth-child(3) svg { color: #0077b5; }
  &:nth-child(4) svg { color: #25d366; }
  &:nth-child(5) svg { color: white; }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 40px;
  margin-bottom: 60px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ArticleWrapper = styled.div`
  background: rgba(75, 46, 131, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  margin-bottom: 40px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  background: rgba(0, 0, 0, 0.1);
  
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    display: block;
  }
  
  &:hover > img {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const ReadTimeBadge = styled.div`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(10px);
`;

const ImageCarousel = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  
  .slick-slider {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
  }
  
  .slick-list {
    height: 100%;
    margin: 0;
    padding: 0;
  }
  
  .slick-track {
    height: 100%;
    display: flex;
    align-items: stretch;
    margin: 0;
    padding: 0;
  }
  
  .slick-slide {
    height: 400px;
    margin: 0;
    padding: 0;
    
    > div {
      height: 100%;
      margin: 0;
      padding: 0;
    }
  }
  
  .slick-dots {
    bottom: 20px;
    z-index: 10;
    
    li {
      margin: 0 4px;
      
      button:before {
        color: white;
        font-size: 10px;
        opacity: 0.5;
      }
      
      &.slick-active button:before {
        color: white;
        opacity: 1;
      }
    }
  }
  
  .slick-arrow {
    z-index: 10;
    width: 40px;
    height: 40px;
    
    &:before {
      font-size: 20px;
      color: white;
      opacity: 0.8;
    }
    
    &:hover:before {
      opacity: 1;
    }
    
    &.slick-prev {
      left: 20px;
    }
    
    &.slick-next {
      right: 20px;
    }
  }
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  display: block;
  margin: 0;
  padding: 0;
`;

const SingleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  background: rgba(0, 0, 0, 0.1);
  
  &::before {
    content: 'Image not available';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
  }
`;

const TagsSection = styled.div`
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const TagsLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: white;
`;

const TagBadge = styled.span`
  display: inline-block;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 20px;
  text-align: center;
  min-height: 60vh;
  
  p {
    margin-top: 20px;
    color: white;
    font-size: 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  padding: 80px 20px;
  text-align: center;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  h2 {
    color: white;
    margin-bottom: 20px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 30px;
    font-size: 1rem;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoItem = styled.li`
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  
  &:last-child {
    border-bottom: none;
  }
  
  strong {
    color: white;
    margin-right: 8px;
  }
`;

// Carousel Arrow Components
const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:focus {
    outline: none;
  }
`;

/* eslint-disable no-unused-vars */
// Memoize arrow components to prevent carousel re-initialization
const PrevArrow = React.memo(({ onClick }) => (
  <ArrowButton onClick={onClick} style={{ left: '20px' }}>
    <FaChevronLeft />
  </ArrowButton>
));

const NextArrow = React.memo(({ onClick }) => (
  <ArrowButton onClick={onClick} style={{ right: '20px' }}>
    <FaChevronRight />
  </ArrowButton>
));

const ArticleContent = styled.div`
  line-height: 1.8;
  color: white;
  
  h3 {
    font-size: 24px;
    font-weight: 800;
    margin: 40px 0 20px;
    color: white;
    position: relative;
    
    &::before {
      content: '';
      position: absolute;
      left: -20px;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background: white;
      border-radius: 2px;
    }
  }
  
  h4 {
    font-size: 20px;
    font-weight: 600;
    margin: 30px 0 15px;
    color: white;
  }
  
  p {
    font-size: 16px;
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.9);
  }
  
  ul {
    margin: 20px 0;
    padding-left: 30px;
    
    li {
      font-size: 16px;
      margin-bottom: 10px;
      color: rgba(255, 255, 255, 0.9);
      
      strong {
        color: white;
        font-weight: 600;
      }
    }
  }
  
  blockquote {
    border-left: 4px solid white;
    padding-left: 20px;
    margin: 30px 0;
    font-style: italic;
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.1);
    padding: 20px;
    border-radius: 8px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  position: sticky;
  top: 120px;
  height: fit-content;
`;

const SidebarCard = styled.div`
  background: rgba(75, 46, 131, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }
`;

const SidebarTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 30px;
    height: 3px;
    background: white;
    border-radius: 2px;
  }
`;

const TOCList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TOCItem = styled.li`
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    color: white;
    padding-left: 8px;
  }
`;

const RelatedArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RelatedArticleItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(75, 46, 131, 0.05);
  }
`;

const RelatedArticleImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RelatedArticleContent = styled.div`
  flex: 1;
  
  h4 {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 4px 0;
    color: white;
    line-height: 1.3;
  }
  
  p {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: rgba(75, 46, 131, 0.05);
    transform: translateX(4px);
  }
  
  svg {
    font-size: 18px;
    color: white;
  }
  
  &:nth-child(1) svg { color: #1877f2; }
  &:nth-child(2) svg { color: #1da1f2; }
  &:nth-child(3) svg { color: #0077b5; }
  &:nth-child(4) svg { color: #25d366; }
`;

const AuthorSection = styled.div`
  margin: 60px 0;
  padding: 40px;
  background: rgba(75, 46, 131, 0.95);
  border-radius: 12px;
  color: white;
`;

const AuthorCard = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const AuthorAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #4b2e83;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(11,9,59,0.1);
`;

const AuthorInfo = styled.div`
  flex: 1;
  color: white;
  
  h4, p {
    color: white;
  }
`;

const RelatedPosts = styled.div`
  margin: 60px 0;
  color: white;
  
  h3 {
    margin-bottom: 30px;
    text-align: center;
    color: white;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedCard = styled.div`
  background: #4b2e83;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(11,9,59,0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(11,9,59,0.15);
  }
`;

const RelatedImage = styled.div`
  width: 100%;
  height: 150px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RelatedContent = styled.div`
  padding: 20px;
`;

const RelatedTag = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  margin-bottom: 10px;
`;

const CallToAction = styled.div`
  text-align: center;
  margin: 80px 0;
  padding: 60px 40px;
  background: #4b2e83;
  border-radius: 16px;
  color: white;
  
  h3 {
    color: white;
    margin-bottom: 20px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 30px;
  }
`;

const CommentsSection = styled.div`
  margin: 60px 0;
  padding: 40px;
  background: rgba(75, 46, 131, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CommentsHeader = styled.div`
  margin-bottom: 30px;
  text-align: center;
  
  p {
    margin-top: 8px;
  }
`;

const CommentForm = styled.form`
  margin-bottom: 40px;
`;

const CommentInputWrapper = styled.div`
  position: relative;
`;

const CommentInput = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 20px;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 16px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  background: #f8f8ff;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    box-shadow: 0 0 0 3px rgba(75, 46, 131, 0.1);
    background: #f8f8ff;
  }
  
  &::placeholder {
    color: #999;
  }
`;

const CommentActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`;

const CommentButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #f8f8ff;
  color: #4b2e83;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(75, 46, 131, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover:not(:disabled) svg {
    transform: translateX(2px);
  }
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CommentItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px;
  background: rgba(75, 46, 131, 0.7);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  color: white;
  
  &:hover {
    background: rgba(75, 46, 131, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  }
`;

const CommentAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(75, 46, 131, 0.1);
  background: #4b2e83;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`;

const CommentsLoading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  
  p {
    margin-top: 20px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }
`;

const EmptyComments = styled.div`
  padding: 60px 20px;
  text-align: center;
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    font-style: italic;
  }
`;

const CommentContent = styled.div`
  flex: 1;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  
  .user-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  
  .user-badge {
    font-size: 11px;
    color: white;
    background: rgba(255, 255, 255, 0.2);
    padding: 2px 8px;
    border-radius: 10px;
    font-weight: 600;
    width: fit-content;
  }
  
  .comment-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
`;

const CommentText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin: 0 0 16px 0;
  font-size: 15px;
`;

const CommentReplies = styled.div`
  margin-top: 16px;
  padding-left: 20px;
  border-left: 2px solid rgba(75, 46, 131, 0.1);
`;

const ReplyItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(75, 46, 131, 0.7);
  border-radius: 12px;
  margin-bottom: 12px;
`;

const ReplyAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #4b2e83;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
`;

const ReplyContent = styled.div`
  flex: 1;
`;

const ReplyHeader = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
`;

const ReplyText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  margin: 0;
  font-size: 14px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: white;
    color: white;
  }
  
  svg {
    font-size: 12px;
  }
`;
/* eslint-enable no-unused-vars */

const LoadMoreComments = styled.div`
  text-align: center;
  margin-top: 30px;
`;

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  border: 2px solid rgba(75, 46, 131, 0.2);
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  color: #1e3a8a;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(75, 46, 131, 0.05);
    border-color: #4b2e83;
    transform: translateY(-2px);
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateY(2px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  
  @media (max-width: 560px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(5px);
`;

const CommentModal = styled.div`
  background: white;
  border-radius: 20px;
  padding: 25px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #333;
  overflow: visible;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  h3 {
    color: #4b2e83;
    margin: 0;
    font-size: 20px;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #666;
  font-size: 24px;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #f8f8ff;
  color: #333;
  font-size: 14px;
  font-family: inherit;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    background: white;
  }
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #f8f8ff;
  color: #333;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  transition: all 0.3s ease;
  box-sizing: border-box;
  min-height: 80px;
  max-height: 120px;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    outline: none;
    border-color: #4b2e83;
    background: white;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  &:not(.cancel) {
    background: #4b2e83;
    color: white;
    
    &:hover:not(:disabled) {
      background: #3a2366;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(75, 46, 131, 0.3);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      background: #ccc;
    }
  }
  
  &.cancel {
    background: transparent;
    color: #666;
    border: 2px solid #e0e0e0;
    
    &:hover {
      background: #f5f5f5;
      border-color: #ccc;
      color: #333;
    }
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${props => props.type === 'success' ? '#4caf50' : '#f44336'};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 2000;
  min-width: 300px;
  max-width: 400px;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
`;

const NotificationMessage = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const NotificationClose = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;
